import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MarketingNav from '../components/layout/MarketingNav';
import MarketingFooter from '../components/layout/MarketingFooter';

/**
 * BenefitsPage — Mental Health Focused Rewrite
 * Outcomes framed around mental health, emotional regulation, stability, coping.
 * Mental wellness includes emotional, mental, and physical wellbeing — not addiction-specific.
 */

const BRAND = {
  forest: '#2D5A47', forestDark: '#1E3D30', forestLight: '#4A7C67',
  sand: '#D4A574', sandLight: '#FFF8F0',
  blue: '#7BA3B8', blueLight: '#EDF4F8',
  dark: '#1A1A2E', text: '#2C2C2C', muted: '#6B6B7B',
  light: '#F8F6F3', white: '#FFFFFF',
};

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function AnimatedStat({ value, label, suffix = '', prefix = '', color, inView }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const target = parseFloat(value);
    const isDecimal = value.toString().includes('.');
    const duration = 1600;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current = Math.min(current + increment, target);
      setDisplay(isDecimal ? parseFloat(current.toFixed(1)) : Math.floor(current));
      if (current >= target) clearInterval(timer);
    }, duration / steps);
    return () => clearInterval(timer);
  }, [inView, value]);

  return (
    <div style={{ textAlign: 'center', padding: '24px' }}>
      <div style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: '900', color, lineHeight: 1, marginBottom: '8px', fontVariantNumeric: 'tabular-nums' }}>
        {prefix}{display}{suffix}
      </div>
      <div style={{ fontSize: '0.85rem', color: BRAND.muted, lineHeight: '1.5', maxWidth: '160px', margin: '0 auto', fontWeight: '500' }}>
        {label}
      </div>
    </div>
  );
}

const outcomeStats = [
  { value: '58', suffix: '%', label: 'reduction in anxiety symptoms with consistent CBT practice', color: BRAND.forest },
  { value: '47', suffix: '%', label: 'improvement in emotional regulation with daily DBT skills', color: BRAND.blue },
  { value: '3', prefix: '', suffix: 'x', label: 'more effective when behavioral tools are used daily vs. weekly', color: BRAND.sand },
  { value: '73', suffix: '%', label: 'of adults report better sleep quality with structured movement', color: BRAND.forestLight },
];

const benefitAreas = [
  {
    area: 'Emotional Regulation',
    icon: '🌊',
    color: BRAND.forest,
    light: '#E8F7F3',
    headline: 'Feel the feeling without being ruled by it.',
    description: 'The Mind pillar builds the specific skills — distress tolerance, emotional identification, cognitive reframing — that turn emotional overwhelm into something manageable. Not by suppressing it. By learning to move through it.',
    outcomes: [
      'Reduced emotional reactivity over time',
      'Greater tolerance for uncomfortable feelings',
      'Improved ability to identify what you\'re actually feeling',
      'Less time stuck in rumination or avoidance',
    ],
    research: 'Grounded in Dialectical Behavior Therapy (DBT) — the most extensively validated framework for emotional regulation disorders.',
  },
  {
    area: 'Mental Clarity & Cognitive Function',
    icon: '🔍',
    color: BRAND.blue,
    light: BRAND.blueLight,
    headline: 'Think more clearly under pressure.',
    description: 'Anxiety, depression, and chronic stress all impair the prefrontal cortex — the part of the brain responsible for decision-making and clear thinking. Daily CBT and ACT practice gradually restores that function.',
    outcomes: [
      'Reduced cognitive distortions and negative self-talk',
      'More accurate perception of threat and safety',
      'Improved problem-solving under stress',
      'Greater mental flexibility and perspective-taking',
    ],
    research: 'Cognitive Behavioral Therapy (CBT) has 50+ years of randomized controlled trials showing significant reduction in depressive and anxious cognition.',
  },
  {
    area: 'Physical Wellbeing & Nervous System',
    icon: '⚡',
    color: BRAND.sand,
    light: BRAND.sandLight,
    headline: 'Your body is part of your mental health.',
    description: 'The Move pillar isn\'t exercise for its own sake. Each activity is chosen for its neurological impact — cortisol reduction, dopamine regulation, vagal nerve activation. The research on movement as mental health intervention is unambiguous.',
    outcomes: [
      'Reduced cortisol and physiological stress response',
      'Improved sleep quality and energy regulation',
      'Nervous system regulation and reduced hypervigilance',
      'Neurological regulation and mood elevation',
    ],
    research: 'Exercise shows equivalent effectiveness to antidepressants for mild-to-moderate depression in multiple meta-analyses (Blumenthal et al., 2007).',
  },
  {
    area: 'Self-Awareness & Insight',
    icon: '📖',
    color: '#8B6E45',
    light: '#FDF6EE',
    headline: 'Know yourself well enough to change.',
    description: 'The Journal pillar builds a documented record of your inner life — moods, patterns, triggers, and growth. Over 365 days, you accumulate the kind of self-knowledge that makes lasting change possible.',
    outcomes: [
      'Documented mood and wellness patterns over time',
      'Clearer understanding of personal triggers',
      'Accountability without judgment or surveillance',
      'A permanent record of growth you can reference',
    ],
    research: 'Expressive writing reduces psychological distress and improves immune function (Pennebaker & Chung, 2011); daily mood tracking improves clinical outcomes.',
  },
];

const forWhom = [
  { condition: 'Anxiety & Panic', icon: '😰', description: 'Daily CBT tools for recognizing and interrupting the anxiety cycle. Move practices that directly down-regulate the stress response.' },
  { condition: 'Depression', icon: '🌧️', description: 'Behavioral activation, cognitive restructuring, and daily movement that interrupts depressive cycles — even when motivation is low.' },
  { condition: 'Grief & Loss', icon: '💔', description: 'Structured space to process loss at your own pace, with daily practices that support both emotional expression and forward movement.' },
  { condition: 'Trauma & PTSD', icon: '🛡️', description: 'Grounding techniques, nervous system regulation, and trauma-informed journaling. Not a trauma processing program — a stability foundation.' },
  { condition: 'Life Transitions', icon: '🔄', description: 'Divorce, job loss, relocation, retirement, identity change — structured daily practice for the disorientation that comes with major change.' },
  { condition: 'Behavioral & Wellness Support', icon: '🌱', description: 'Mood tracking, behavioral patterns, mindfulness practices, and the daily accountability structure that supports lasting positive change.' },
  { condition: 'Burnout & Chronic Stress', icon: '🔋', description: 'Nervous system regulation, boundary development, and sustainable daily practices for people running on empty.' },
  { condition: 'Emotional Wellness', icon: '🌿', description: 'For anyone healing from a difficult relationship, a painful experience, or years of not having the right tools for their inner life.' },
];

const BenefitsPage = () => {
  const navigate = useNavigate();
  const [statsRef, statsInView] = useInView(0.2);

  return (
    <div className="site-canvas" style={{ fontFamily: 'system-ui, -apple-system, sans-serif', color: BRAND.text, minHeight: '100vh' }}>
      <MarketingNav transparent={true} />

      {/* ── HERO ── */}
      <section className="marketing-photo-hero" style={{ backgroundImage: "url('/couch.png')" }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" style={{ maxWidth: '1280px', paddingTop: '130px', paddingBottom: '110px' }}>
          <div className="marketing-photo-hero-panel" style={{ maxWidth: '700px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,209,102,0.15)', border: '1px solid rgba(255,209,102,0.3)', borderRadius: '100px', padding: '7px 18px', marginBottom: '24px' }}>
            <span style={{ color: '#ffd166', fontSize: '0.8rem', fontWeight: '600', letterSpacing: '0.07em', textTransform: 'uppercase' }}>Why It Works</span>
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.25rem)', fontWeight: '800', color: '#f1fff8', lineHeight: '1.1', letterSpacing: '-0.025em', marginBottom: '20px' }}>
            Mental health built
            <br /><span style={{ color: '#ffd166' }}>one day at a time.</span>
          </h1>
          <p style={{ fontSize: 'clamp(0.95rem, 2vw, 1.15rem)', color: 'rgba(241,255,248,0.80)', lineHeight: '1.7', maxWidth: '520px', margin: '0 0 36px 0' }}>
            Not a quick fix. Not a crisis intervention. A clinically grounded daily practice for
            building the kind of emotional regulation and mental stability that sustains you through
            whatever life brings — with or without a therapist.
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/register')}
              style={{ background: '#ffd166', color: '#041b12', fontWeight: '700', fontSize: '1rem', padding: '14px 32px', borderRadius: '8px', border: 'none', cursor: 'pointer', transition: 'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f2c14e'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#ffd166'; e.currentTarget.style.transform = 'translateY(0)'; }}>
              Start My Daily Practice
            </button>
            <button onClick={() => navigate('/research-references')}
              style={{ background: 'transparent', color: BRAND.white, fontWeight: '600', fontSize: '1rem', padding: '14px 32px', borderRadius: '8px', border: '2px solid rgba(255,255,255,0.3)', cursor: 'pointer', transition: 'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.65)'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; e.currentTarget.style.background = 'transparent'; }}>
              See the Research →
            </button>
          </div>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section ref={statsRef} style={{ background: BRAND.light, padding: 'clamp(48px, 8vw, 80px) 24px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <p style={{ textAlign: 'center', fontSize: '0.8rem', color: BRAND.muted, fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '32px' }}>
            What the research shows — across CBT, DBT, ACT, and exercise neuroscience
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0', borderRadius: '20px', overflow: 'hidden', border: '1px solid #E8E5E1', background: BRAND.white }}>
            {outcomeStats.map((stat, i) => (
              <div key={i} style={{ borderRight: i < outcomeStats.length - 1 ? '1px solid #E8E5E1' : 'none' }}>
                <AnimatedStat {...stat} inView={statsInView} />
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#9A9AAA', marginTop: '16px' }}>
            Statistics drawn from peer-reviewed research. &nbsp;<button onClick={() => navigate('/research')} style={{ color: BRAND.forest, background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem', textDecoration: 'underline' }}>View full research references →</button>
          </p>
        </div>
      </section>

      {/* ── BENEFIT AREAS ── */}
      <section style={{ background: BRAND.white, padding: 'clamp(64px, 10vw, 120px) 24px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <h2 style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2.25rem)', fontWeight: '800', color: BRAND.dark, letterSpacing: '-0.02em', marginBottom: '12px' }}>
              Four domains. One integrated practice.
            </h2>
            <p style={{ fontSize: '1rem', color: BRAND.muted, lineHeight: '1.65', maxWidth: '500px', margin: '0 auto' }}>
              Momentum addresses mental health holistically — not just symptom management, but the underlying skills that make long-term wellbeing possible.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {benefitAreas.map((area, i) => (
              <div key={area.area} style={{
                display: 'grid', gridTemplateColumns: i % 2 === 0 ? '1fr 2fr' : '2fr 1fr',
                gap: '48px', alignItems: 'center', background: area.light,
                borderRadius: '20px', padding: 'clamp(28px, 5vw, 48px)',
                border: `1px solid ${area.color}20`,
              }}>
                {i % 2 === 0 ? (
                  <>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>{area.icon}</div>
                      <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: area.color, margin: 0 }}>{area.area}</h3>
                    </div>
                    <div>
                      <p style={{ fontSize: '1rem', fontWeight: '700', color: BRAND.dark, lineHeight: '1.4', marginBottom: '12px' }}>{area.headline}</p>
                      <p style={{ fontSize: '0.9rem', color: BRAND.muted, lineHeight: '1.7', marginBottom: '20px' }}>{area.description}</p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', marginBottom: '16px' }}>
                        {area.outcomes.map(o => (
                          <div key={o} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.82rem', color: '#3A3A4A' }}>
                            <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: area.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                              <span style={{ color: BRAND.white, fontSize: '0.55rem', fontWeight: '700' }}>✓</span>
                            </div>
                            {o}
                          </div>
                        ))}
                      </div>
                      <div style={{ background: BRAND.white, borderRadius: '8px', padding: '10px 14px', borderLeft: `3px solid ${area.color}` }}>
                        <p style={{ fontSize: '0.78rem', color: '#5A5A6E', lineHeight: '1.5', margin: 0, fontStyle: 'italic' }}>{area.research}</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p style={{ fontSize: '1rem', fontWeight: '700', color: BRAND.dark, lineHeight: '1.4', marginBottom: '12px' }}>{area.headline}</p>
                      <p style={{ fontSize: '0.9rem', color: BRAND.muted, lineHeight: '1.7', marginBottom: '20px' }}>{area.description}</p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', marginBottom: '16px' }}>
                        {area.outcomes.map(o => (
                          <div key={o} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.82rem', color: '#3A3A4A' }}>
                            <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: area.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                              <span style={{ color: BRAND.white, fontSize: '0.55rem', fontWeight: '700' }}>✓</span>
                            </div>
                            {o}
                          </div>
                        ))}
                      </div>
                      <div style={{ background: BRAND.white, borderRadius: '8px', padding: '10px 14px', borderLeft: `3px solid ${area.color}` }}>
                        <p style={{ fontSize: '0.78rem', color: '#5A5A6E', lineHeight: '1.5', margin: 0, fontStyle: 'italic' }}>{area.research}</p>
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>{area.icon}</div>
                      <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: area.color, margin: 0 }}>{area.area}</h3>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO IT'S FOR ── */}
      <section style={{ background: BRAND.light, padding: 'clamp(64px, 10vw, 120px) 24px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2.25rem)', fontWeight: '800', color: BRAND.dark, letterSpacing: '-0.02em', marginBottom: '12px' }}>
              Who Momentum is for
            </h2>
            <p style={{ fontSize: '1rem', color: BRAND.muted, lineHeight: '1.65', maxWidth: '480px', margin: '0 auto' }}>
              For anyone who needs structured daily support for their mental and emotional health between sessions with their clinician, clergy, or life coach.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            {forWhom.map(item => (
              <div key={item.condition} style={{ background: BRAND.white, borderRadius: '16px', padding: '24px', border: '1px solid #E8E5E1', transition: 'box-shadow 0.2s, transform 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 28px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                <div style={{ fontSize: '1.75rem', marginBottom: '10px' }}>{item.icon}</div>
                <h3 style={{ fontSize: '0.95rem', fontWeight: '800', color: BRAND.dark, marginBottom: '8px' }}>{item.condition}</h3>
                <p style={{ fontSize: '0.82rem', color: BRAND.muted, lineHeight: '1.6', margin: 0 }}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="hero-marble-bg" style={{ padding: 'clamp(64px, 10vw, 100px) 24px' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: '800', color: '#f1fff8', lineHeight: '1.15', letterSpacing: '-0.02em', marginBottom: '16px' }}>
            The research is there.
            <br /><span style={{ color: '#ffd166' }}>So is the program.</span>
          </h2>
          <p style={{ fontSize: '1rem', color: 'rgba(241,255,248,0.75)', lineHeight: '1.65', marginBottom: '36px' }}>
            365 days of evidence-based practice. Clinician-compatible. Fully self-guided. Built for the life you're actually living.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/register')}
              style={{ background: '#ffd166', color: '#041b12', fontWeight: '700', fontSize: '1rem', padding: '14px 32px', borderRadius: '8px', border: 'none', cursor: 'pointer', transition: 'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#c49460'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#D4A574'; e.currentTarget.style.transform = 'translateY(0)'; }}>
              Start My Daily Practice
            </button>
            <button onClick={() => navigate('/clinicians')}
              style={{ background: 'transparent', color: BRAND.white, fontWeight: '600', fontSize: '1rem', padding: '14px 32px', borderRadius: '8px', border: '2px solid rgba(255,255,255,0.3)', cursor: 'pointer', transition: 'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.65)'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; e.currentTarget.style.background = 'transparent'; }}>
              I'm a Clinician →
            </button>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default BenefitsPage;
